/**
 * T-S1-A W8+W9 · Orders API client
 * 契约源：apps/api-server/src/api_server/routes/orders.py
 * 14 endpoints: 8 (W8) + 6 (W9)
 */
import { api, apiConfig, ApiError } from "./api";
import { useAuthStore } from "@/store/authStore";
import type { DispatchOrderRead } from "./dispatch";
import type { SmsOptOutStatus, SmsOptOutTogglePayload } from "./types/smsOptOut";
import type {
  OrderAssignPayload,
  OrderDispatchPayload,
  OrderCalendarDayResponse,
  OrderCalendarItemType,
  OrderCalendarResponse,
  OrderCalendarSegmentsResponse,
  OrderCancelPayload,
  OrderCompleteCheckoutPayload,
  OrderCompleteCheckoutResponse,
  OrderConfirmPayload,
  OrderCreatePayload,
  OrderFinancials,
  OrderIssueVoucherPayload,
  OrderItemCreatePayload,
  OrderItemRead,
  OrderItemUpdatePayload,
  TicketChangeOptions,
  OrderListQuery,
  OrderCollectItemsResponse,
  OrderCollectItemSavePayload,
  OrderRead,
  OrderReschedulePayload,
  OrderSettlePayload,
  OrderStats,
  OrderUpdatePayload,
  OrderVoucherRead,
  OrderWithHistory,
  PartnerPendingAdvisory,
  PendingPartnerConfirmItem,
  RejectPartnerOrderPayload,
} from "./types/orders";

const BASE = "/api/v1/orders";

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ── W8 endpoints ──────────────────────────────────────────────────────────────

/** GYGUX-1 · GET /api/v1/orders/_channel-platforms · 来源平台清单（订单列表「渠道」筛选下拉用） */
export function listChannelPlatforms(): Promise<{ code: string; name: string }[]> {
  return api(`${BASE}/_channel-platforms`);
}

/** GET /api/v1/orders/_stats */
export function getOrderStats(): Promise<OrderStats> {
  return api(`${BASE}/_stats`);
}

/** GET /api/v1/orders/_calendar · 订单日历聚合（page-8 酒店 / page-35 门票）*/
export function getOrderCalendar(
  itemType: OrderCalendarItemType,
  dateFrom: string,
  dateTo: string,
  opts?: { resourceId?: string | null; subResourceId?: string | null; includeOrders?: boolean },
): Promise<OrderCalendarResponse> {
  const qs = buildQuery({
    item_type: itemType, date_from: dateFrom, date_to: dateTo,
    resource_id: opts?.resourceId ?? undefined,
    sub_resource_id: opts?.subResourceId ?? undefined,
  });
  return api(`${BASE}/_calendar${qs}`);
}

/** GET /api/v1/orders/_calendar/day · 当日订单明细（drawer）*/
export function getOrderCalendarDay(
  itemType: OrderCalendarItemType,
  day: string,
  opts?: { resourceId?: string | null; subResourceId?: string | null; includeOrders?: boolean },
): Promise<OrderCalendarDayResponse> {
  const qs = buildQuery({
    item_type: itemType, day,
    resource_id: opts?.resourceId ?? undefined,
    sub_resource_id: opts?.subResourceId ?? undefined,
  });
  return api(`${BASE}/_calendar/day${qs}`);
}

/** GET /api/v1/orders/_calendar/segments · 多段时间汇总（合并下载 PDF）*/
export function getOrderCalendarSegments(
  itemType: OrderCalendarItemType,
  ranges: { from: string; to: string }[],
  opts?: { resourceId?: string | null; subResourceId?: string | null; includeOrders?: boolean },
): Promise<OrderCalendarSegmentsResponse> {
  const qs = buildQuery({
    item_type: itemType,
    ranges: ranges.map((r) => `${r.from}:${r.to}`).join(","),
    resource_id: opts?.resourceId ?? undefined,
    sub_resource_id: opts?.subResourceId ?? undefined,
    include_orders: opts?.includeOrders ? "true" : undefined, // 批次8·PDF 对账明细行
  });
  return api(`${BASE}/_calendar/segments${qs}`);
}

/** GET /api/v1/orders */
/** @param opts.paged GUARD · 调用方是带 UI 分页器的列表页时传 true（消除静默截断告警） */
export function listOrders(
  q?: OrderListQuery,
  opts?: { paged?: boolean },
): Promise<OrderRead[]> {
  const qs = buildQuery((q ?? {}) as Record<string, unknown>);
  return api(`${BASE}${qs}`, { paged: opts?.paged });
}

/**
 * 列表 + 筛选后总数（X-Total-Count 响应头）。
 * 2026-08-06 修复：列表页「共 N 单」此前误用全局 stats.total，
 * 一筛状态数字就与快捷徽标对不上——筛选后总数必须来自与行查询同一套 where。
 */
export async function listOrdersWithTotal(
  q?: OrderListQuery,
): Promise<{ items: OrderRead[]; total: number | null }> {
  const qs = buildQuery((q ?? {}) as Record<string, unknown>);
  let total: number | null = null;
  const items = await api<OrderRead[]>(`${BASE}${qs}`, {
    paged: true,
    onHeaders: (h) => {
      const v = h.get("X-Total-Count");
      total = v != null ? Number(v) : null;
    },
  });
  return { items, total };
}

/** POST /api/v1/orders */
export function createOrder(payload: OrderCreatePayload): Promise<OrderRead> {
  return api(BASE, { method: "POST", body: JSON.stringify(payload) });
}

/** GET /api/v1/orders/{id} */
export function getOrder(id: string): Promise<OrderWithHistory> {
  return api(`${BASE}/${id}`);
}

/** PTICKET-1 · 门票确认信（中英双语打印页）：带登录态取回 HTML 再新窗口打开，页里自带「打印 / 存为 PDF」 */
export async function openTicketLetter(id: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${apiConfig.base}${BASE}/${id}/confirmation-letter`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, res.status === 409 ? "订单尚未确认，确认后才有确认信" : `打开确认信失败（${res.status}）`);
  window.open(URL.createObjectURL(new Blob([await res.text()], { type: "text/html;charset=utf-8" })), "_blank");
}

/** PATCH /api/v1/orders/{id} */
export function updateOrder(id: string, payload: OrderUpdatePayload): Promise<OrderRead> {
  return api(`${BASE}/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
}

/** GET /api/v1/orders/_assignable-ops · 可指派 OP 列表（主管门·ORDIMP）*/
export interface AssignableOp {
  id: string;
  name: string;
  email: string | null;
  primary_role: string | null;
}
export function listAssignableOps(): Promise<AssignableOp[]> {
  return api(`${BASE}/_assignable-ops`);
}

/** DELETE /api/v1/orders/{id} */
export function deleteOrder(id: string): Promise<void> {
  return api(`${BASE}/${id}`, { method: "DELETE" });
}

/** POST /api/v1/orders/{id}/confirm */
export function confirmOrder(id: string, payload?: OrderConfirmPayload): Promise<OrderRead> {
  return api(`${BASE}/${id}/confirm`, { method: "POST", body: JSON.stringify(payload ?? {}) });
}

/** POST /api/v1/orders/{id}/assign */
export function assignOrder(id: string, payload: OrderAssignPayload): Promise<OrderRead> {
  return api(`${BASE}/${id}/assign`, { method: "POST", body: JSON.stringify(payload) });
}

/** POST /api/v1/orders/{id}/unassign — 取消派单（清司导/车辆+作废派车单·回 confirmed）*/
export function unassignOrder(id: string): Promise<OrderRead> {
  return api(`${BASE}/${id}/unassign`, { method: "POST" });
}

/** CONSENT-1 · 短信退订/撤销退订（按订单客户手机号·sms_opt_outs 表） */
export function toggleOrderSmsOptOut(id: string, payload: SmsOptOutTogglePayload): Promise<SmsOptOutStatus> {
  return api(`${BASE}/${id}/sms-opt-out`, { method: "PATCH", body: JSON.stringify(payload) });
}

/**
 * DISPFIX-1 Part 1 · POST /api/v1/orders/{id}:dispatch —— 派单原子端点（唯一权威实现）。
 * 取代旧的 createDispatchOrder → appendDispatchLeg → assignOrder 三步串调（Bug#2
 * 根因：归属闸在第③步才触发，前两步已落库不回滚）。一次调用、一个事务、失败零残留。
 */
export interface OrderDispatchResult {
  order: OrderRead;
  dispatch: DispatchOrderRead;
}
export function dispatchOrderAtomic(id: string, payload: OrderDispatchPayload): Promise<OrderDispatchResult> {
  return api(`${BASE}/${id}:dispatch`, { method: "POST", body: JSON.stringify(payload) });
}

// ── PARTNER-2C · 同行站待确认队列（改动三）───────────────────────────────────

/** GET /api/v1/orders/_pending-partner-confirm */
export function getPendingPartnerConfirm(): Promise<PendingPartnerConfirmItem[]> {
  return api(`${BASE}/_pending-partner-confirm`);
}

/** GET /api/v1/orders/partner-pending/{id}/advisory · 判读结果/团期已收人数/车型断点提示 */
export function getPartnerPendingAdvisory(id: string): Promise<PartnerPendingAdvisory> {
  return api(`${BASE}/partner-pending/${id}/advisory`);
}

// ── ADV-1 · 决策辅助泛化到所有 draft 订单（不限来源）─────────────────────────

/** GET /api/v1/orders/{id}/advisory · 仅 draft 单可调，非 draft 409（同 getPartnerPendingAdvisory
 * 同一后端 handler，只是不限定 source=partner_portal）。 */
export function getOrderAdvisory(id: string): Promise<PartnerPendingAdvisory> {
  return api(`${BASE}/${id}/advisory`);
}

/** POST /api/v1/orders/{id}/reject-partner-order */
export function rejectPartnerOrder(id: string, payload: RejectPartnerOrderPayload): Promise<OrderRead> {
  return api(`${BASE}/${id}/reject-partner-order`, { method: "POST", body: JSON.stringify(payload) });
}

// ── W9 endpoints ──────────────────────────────────────────────────────────────

/** POST /api/v1/orders/{id}/issue-voucher */
export function issueVoucher(id: string, payload?: OrderIssueVoucherPayload): Promise<OrderRead> {
  return api(`${BASE}/${id}/issue-voucher`, { method: "POST", body: JSON.stringify(payload ?? {}) });
}

/** GET /api/v1/orders/{id}/voucher */
export function getVoucher(id: string): Promise<OrderVoucherRead> {
  return api(`${BASE}/${id}/voucher`);
}

// ── VSEND 工单 · 凭证发送 ─────────────────────────────────────────────────────

export interface VoucherSendPayload {
  channels: ("email" | "sms")[];
  to_email?: string | null;
  to_phone?: string | null;
  note?: string | null;
}

// 🔴 status 三态之一：'sent'（真发出）/ 'failed'（真失败·有 failure_reason）/
// 'mocked'（通道未真接通·绝不能当成功显示——见工单 §3 红线）。
export interface VoucherDeliveryRecord {
  id: string;
  channel: "email" | "sms";
  recipient: string;
  status: "sent" | "failed" | "mocked" | "pending" | "delivered" | "read" | "bounced";
  failure_reason: string | null;
  sent_at: string | null;
  created_at?: string;
}

/** GET /api/v1/orders/{id}/voucher/name-check · ENNAME-3：英文客人的凭证涉及缺英文名的酒店 → warnings（不拦，可继续发送） */
export function getVoucherNameCheck(id: string): Promise<{ warnings: string[] }> {
  return api(`${BASE}/${id}/voucher/name-check`);
}

/** POST /api/v1/orders/{id}/voucher:send */
export function sendVoucher(id: string, payload: VoucherSendPayload): Promise<{ results: VoucherDeliveryRecord[] }> {
  return api(`${BASE}/${id}/voucher:send`, { method: "POST", body: JSON.stringify(payload) });
}

/** GET /api/v1/orders/{id}/voucher/deliveries */
export function getVoucherDeliveries(id: string): Promise<{ items: VoucherDeliveryRecord[] }> {
  return api(`${BASE}/${id}/voucher/deliveries`);
}

/** 出团确认单数据（ORDSTAT Part C · 对外文书·无酒店/司导）*/
export interface ConfirmationData {
  product_title: string;
  booking_number: string;
  confirmation_number: string;
  tour_date: string | null;
  customer_name: string;
  pax_count: number;
  pax_detail: string | null;
  contact: string | null;
  pickup_location: string;
  pickup_time_note: string;
  after_sales_block: { wechat?: string; cn_phone?: string; us_phone?: string; whatsapp?: string; email?: string };
  terms: { zh: string; en: string }[];
  footer_phone: string;
  company_name: string;
}

/** GET /api/v1/orders/{id}/confirmation */
export function getConfirmation(id: string): Promise<ConfirmationData> {
  return api(`${BASE}/${id}/confirmation`);
}

/** POST /api/v1/orders/{id}/settle */
export function settleOrder(id: string, payload?: OrderSettlePayload): Promise<OrderRead> {
  return api(`${BASE}/${id}/settle`, { method: "POST", body: JSON.stringify(payload ?? {}) });
}

/** GET /api/v1/orders/{id}/financials */
export function getOrderFinancials(id: string): Promise<OrderFinancials> {
  return api(`${BASE}/${id}/financials`);
}

/** GET /api/v1/orders/{id}/cost-breakdown — PEND-1 A5 · 成本 / 毛利仅老板 / 财务 / 超管可见（403） */
export interface OrderCostBreakdown {
  product_id: string;
  product_code: string;
  pax: number;
  occupancy: number;
  total_days: number;
  total_nights: number;
  vehicle_label: string;
  vehicle_seats: number;
  fleet_daily_cost_cents: number;
  fleet_per_person_cents: number;
  hotel_per_person_cents: number;
  ticket_total_cents: number;   // 门票过路账（只含自费 / 必付景点；TIXSEL-1 起「已含」的票不在这里）
  ticket_included_per_person_cents?: number;   // TIXSEL-1 · 已含门票（选定票种）每人成本，已计入套餐成本
  package_cost_per_person_cents: number;
  mandatory_fee_cents: number;
  markup_pct: number;   // MARKUP-1 · 建议价用的成本加成率%（原 target_margin_pct）
  suggested_price_per_person_cents: number;
  notes: string[];
  // TRANSCOST-1 · 接送机整趟进本单成本（不乘人数）
  bus_days?: number;
  transfer_per_person_cents?: number;
  transfer_trip_total_cents?: number;
  transfer_cost_missing?: boolean;
}
export function getOrderCostBreakdown(id: string): Promise<OrderCostBreakdown> {
  return api(`${BASE}/${id}/cost-breakdown`);
}

/** POST /api/v1/orders/{id}/cancel */
export function cancelOrder(id: string, payload: OrderCancelPayload): Promise<OrderRead> {
  return api(`${BASE}/${id}/cancel`, { method: "POST", body: JSON.stringify(payload) });
}

/** POST /api/v1/orders/{id}/assign-op — 重指/认领负责 OP（方案 A · 交接）
 *  opUserId=null → 释放为未归属 */
export function assignOrderOp(id: string, opUserId: string | null): Promise<OrderRead> {
  return api(`${BASE}/${id}/assign-op`, {
    method: "POST",
    body: JSON.stringify({ op_user_id: opUserId }),
  });
}

/** POST /api/v1/orders/{id}:reschedule — 改期/转团/改人数/改住房档（HOTFIX·原子重算库存·仅 confirmed/assigned） */
export function rescheduleOrder(id: string, payload: OrderReschedulePayload): Promise<OrderRead> {
  return api(`${BASE}/${id}:reschedule`, { method: "POST", body: JSON.stringify(payload) });
}

/** POST /api/v1/orders:complete-checkout (API-First 组合端点) */
export function completeCheckout(
  payload: OrderCompleteCheckoutPayload,
): Promise<OrderCompleteCheckoutResponse> {
  return api(`${BASE}:complete-checkout`, { method: "POST", body: JSON.stringify(payload) });
}

// ── R-08 预订行端点（order_items · 仅 draft 可增删改）─────────────────────────────

/** GET /api/v1/orders/{id}/items — 预订行列表 */
export function getOrderItems(id: string): Promise<OrderItemRead[]> {
  return api(`${BASE}/${id}/items`);
}

/** POST /api/v1/orders/{id}/items — 增预订行（仅 draft · 201） */
export function addOrderItem(
  id: string,
  payload: OrderItemCreatePayload,
): Promise<OrderItemRead> {
  return api(`${BASE}/${id}/items`, { method: "POST", body: JSON.stringify(payload) });
}

/** PATCH /api/v1/orders/{id}/items/{itemId} — 改预订行（OP 选房型 / 改数量价 · 仅 draft） */
export function updateOrderItem(
  id: string,
  itemId: string,
  payload: OrderItemUpdatePayload,
): Promise<OrderItemRead> {
  return api(`${BASE}/${id}/items/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** GET /api/v1/orders/{id}/items/{itemId}/ticket-options — TIXSEL-1 · 门票行可改的票种（出发当天有库存的 + 当前这张） */
export function getTicketChangeOptions(id: string, itemId: string): Promise<TicketChangeOptions> {
  return api(`${BASE}/${id}/items/${itemId}/ticket-options`);
}

/** DELETE /api/v1/orders/{id}/items/{itemId} — 删预订行（仅 draft · 204） */
export function deleteOrderItem(id: string, itemId: string): Promise<void> {
  return api(`${BASE}/${id}/items/${itemId}`, { method: "DELETE" });
}

// ── DRVCOLLECT 订单代收清单（不是收款确认闭环，见工单 §0.4）──────────────────

/** GET /api/v1/orders/{id}/collect-items — 司导代收/公司收分列合计 */
export function getOrderCollectItems(id: string): Promise<OrderCollectItemsResponse> {
  return api(`${BASE}/${id}/collect-items`);
}

/** PUT /api/v1/orders/{id}/collect-items — 整块保存（增删改·门票行固定司导代收） */
export function saveOrderCollectItems(
  id: string,
  items: OrderCollectItemSavePayload[],
): Promise<OrderCollectItemsResponse> {
  return api(`${BASE}/${id}/collect-items`, { method: "PUT", body: JSON.stringify({ items }) });
}

// ── ORD1 订单自由留言 / 交接记录（op_audit_logs · action=note）──────────────────

/** 一条订单自由留言（契约源：routes/orders.py · OrderNoteRead） */
export interface OrderNote {
  id: string;
  text: string;
  actor_name: string;
  actor_user_id: string | null;
  at: string | null;
}

/** GET /api/v1/orders/{id}/notes — 留言列表（登录即可读·非 admin-gated·倒序） */
export function listOrderNotes(id: string): Promise<OrderNote[]> {
  return api(`${BASE}/${id}/notes`);
}

/** POST /api/v1/orders/{id}/notes — 加自由留言（负责 OP / 未归属 / 主管 · 201） */
export function addOrderNote(id: string, text: string): Promise<OrderNote> {
  return api(`${BASE}/${id}/notes`, { method: "POST", body: JSON.stringify({ text }) });
}

// ── HOTELENH · 政策截止（取消截止 + Rooming List 截止）只读端点 ────────────────────
// 一个端点喂 ② 订单日历角标 + ④ Rooming List 提醒（首页 todo + 订单列表）。
// 门=登录即可（读·无金额）· 两存储覆盖（self_contract 取消存 room_type · hold 存 policy）。

/** 单行：一个 hotel order_item 的取消截止 + Rooming List 截止（契约源：routes/orders.py · PolicyDeadlineRow） */
export interface PolicyDeadlineRow {
  order_id: string;
  order_no: string;
  hotel_name: string | null;
  room_type_label: string | null;
  check_in_date: string; // YYYY-MM-DD
  cancel_deadline: string | null; // 入住日 − 取消窗口天 · 无配置=null
  rooming_deadline: string | null; // 入住日 − rooming_list_days · 无配置=null
  rooming_list_submitted_at: string | null; // NULL=未提交
}

/** GET /api/v1/orders/_policy-deadlines?from=&to= — 截止日落在 [from,to] 的 hotel 预订行 */
export function getPolicyDeadlines(from: string, to: string): Promise<PolicyDeadlineRow[]> {
  const qs = buildQuery({ from, to });
  return api(`${BASE}/_policy-deadlines${qs}`);
}

/** POST /api/v1/orders/{id}/rooming-list-submitted — 标记名单已提交（可编辑订单者门） */
export function markRoomingListSubmitted(id: string): Promise<OrderRead> {
  return api(`${BASE}/${id}/rooming-list-submitted`, { method: "POST" });
}

/** DELETE /api/v1/orders/{id}/rooming-list-submitted — 撤销名单提交标记 */
export function unmarkRoomingListSubmitted(id: string): Promise<OrderRead> {
  return api(`${BASE}/${id}/rooming-list-submitted`, { method: "DELETE" });
}
